import {
  CHAT_ALERT_CONTENT_CLASS_NAME,
  CHAT_DIALOG_HEADER_CLASS_NAME,
  CHAT_DIALOG_DESCRIPTION_CLASS_NAME,
  CHAT_DIALOG_FOOTER_CLASS_NAME,
} from "./chat-dialog-styles";
import type { ChatThread, ProviderChatThread } from "./types";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import {
  ArrowLeft,
  MoreVertical,
  Archive,
  User,
  UserCircle,
  ExternalLink,
  Briefcase,
  Bell,
  Clock,
} from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { useTRPC } from "@/trpc";
import { useQuery, useMutation } from "@tanstack/react-query";
import { useState, useEffect } from "react";
import { ProfileModal } from "../profile/components/ProfileModal";
import { InterestedJobPostsDialog } from "../job-post/components/interested-job-posts-dialog";
import { isVirtualThreadId } from "./virtual-thread";

export const ChatThreadHeader = ({
  thread,
  onBack,
  onArchive,
  onViewDetails,
  currentUser,
  organizationBusinessUnitId,
  onRefreshThreads,
}: {
  thread: ChatThread;
  onBack: () => void;
  onArchive: () => void;
  onViewDetails: () => void;
  currentUser?: {
    id: string;
    is_admin?: boolean;
  };
  organizationBusinessUnitId?: string;
  onRefreshThreads?: () => Promise<void>;
}) => {
  const hasUserParticipants = thread.user_participants.length > 0;
  const isVirtual = isVirtualThreadId(thread.id);
  const trpc = useTRPC();
  const { data: userDetails } = useQuery(
    trpc.chat.getUserDetails.queryOptions(
      {
        userId: thread.user_participants[0]?.id,
      },
      {
        enabled: hasUserParticipants,
      },
    ),
  );
  const [showDetails, setShowDetails] = useState(false);
  const [showProfileModal, setShowProfileModal] = useState(false);
  const [showInterestedJobPosts, setShowInterestedJobPosts] = useState(false);
  const [showReminderDialog, setShowReminderDialog] = useState(false);

  // Reminder functionality
  const [timeUntilNextReminder, setTimeUntilNextReminder] = useState<
    number | null
  >(null);

  const sendReminderMutation = useMutation(
    trpc.chat.sendChatReminder.mutationOptions({
      onSuccess: async (data) => {
        setTimeUntilNextReminder(60 * 60 * 1000); // 1 hour in milliseconds
        setShowReminderDialog(false);
        await onRefreshThreads?.();

        toast.success("Reminder sent", {
          description:
            "The provider has been notified about your unread message.",
        });
      },
      onError: (error) => {
        console.error("Failed to send reminder:", error);
        setShowReminderDialog(false);

        if (error.data?.code === "TOO_MANY_REQUESTS") {
          // Extract time from error message if possible
          const timeMatch = error.message.match(/until (.+?) before/);
          const waitTime = timeMatch ? timeMatch[1] : "an hour";

          toast.error("Failed to send reminder", {
            description: `You need to wait to send another reminder. You can send another reminder at ${waitTime}. Only one reminder per hour is allowed.`,
            duration: 7000,
            dismissible: true,
          });
        } else {
          toast.error("Failed to send reminder", {
            description:
              "There was an error sending the reminder. Please try again.",
          });
        }
      },
    }),
  );

  // Calculate time until next reminder is available
  useEffect(() => {
    const provider = thread.user_participants[0];
    if (provider?.last_reminder_sent_at) {
      const lastReminderTime = new Date(
        provider.last_reminder_sent_at,
      ).getTime();
      const oneHourLater = lastReminderTime + 60 * 60 * 1000;
      const now = Date.now();

      if (now < oneHourLater) {
        setTimeUntilNextReminder(oneHourLater - now);

        // Set up a timer to update the countdown
        const interval = setInterval(() => {
          const remaining = oneHourLater - Date.now();
          if (remaining <= 0) {
            setTimeUntilNextReminder(null);
            clearInterval(interval);
          } else {
            setTimeUntilNextReminder(remaining);
          }
        }, 60000); // Update every minute

        return () => clearInterval(interval);
      }
    }
  }, [thread.user_participants]);

  // Check if reminder should be shown
  const shouldShowReminder = () => {
    if (!currentUser || !organizationBusinessUnitId) {
      return false;
    }

    const provider = thread.user_participants[0];
    if (!provider) {
      return false;
    }

    // Show reminder option for all conversations with providers
    return true;
  };

  const handleSendReminder = () => {
    setShowReminderDialog(true);
  };

  const confirmSendReminder = () => {
    if (!organizationBusinessUnitId) {
      return;
    }

    sendReminderMutation.mutate({
      threadId: thread.id,
      organizationBusinessUnitId,
    });
  };

  const formatTimeRemaining = (ms: number) => {
    const minutes = Math.ceil(ms / (1000 * 60));
    if (minutes < 60) {
      return `${minutes}m`;
    }
    const hours = Math.floor(minutes / 60);
    const remainingMinutes = minutes % 60;
    return remainingMinutes > 0
      ? `${hours}h ${remainingMinutes}m`
      : `${hours}h`;
  };

  return (
    <div className="flex max-h-[45dvh] min-w-0 shrink-0 flex-col overflow-y-auto border-b border-slate-200 bg-white p-3 sm:p-4">
      <div className="flex min-w-0 items-center gap-2 sm:gap-3">
        <Button
          variant="ghost"
          size="icon"
          onClick={onBack}
          className="h-12 w-12 shrink-0 rounded-xl text-slate-600 lg:hidden"
          aria-label="Back to conversations"
        >
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <Avatar className="hidden h-10 w-10 shrink-0 rounded-xl border border-primary/15 sm:flex">
          <AvatarFallback className="rounded-xl bg-primary/10 text-sm font-semibold text-primary">
            {thread.user_participants[0]?.first_name?.[0]
              ? thread.user_participants[0]?.first_name[0]
              : thread.organization_participants[0]?.name?.[0] || "?"}
          </AvatarFallback>
        </Avatar>
        <div className="flex-1 min-w-0">
          <h2 className="text-base font-semibold leading-6 text-slate-900 [overflow-wrap:anywhere] sm:text-lg">
            {thread.user_participants.length
              ? thread.user_participants
                  .map(
                    (p: ProviderChatThread["user_participants"][number]) =>
                      `${p.first_name} ${p.last_name}`,
                  )
                  .join(", ")
              : thread.organization_participants
                  .map(
                    (
                      p: ProviderChatThread["organization_participants"][number],
                    ) => p.name,
                  )
                  .join(", ")}
          </h2>
          {userDetails?.healthcareProfessionalType && (
            <div className="flex items-center space-x-1 mt-1">
              <div className="h-1.5 w-1.5 shrink-0 rounded-full bg-primary" />
              <p className="min-w-0 text-xs font-medium leading-5 text-primary [overflow-wrap:anywhere] sm:text-sm">
                {userDetails.healthcareProfessionalType.name}
              </p>
            </div>
          )}
          {userDetails?.specialties && userDetails.specialties.length > 0 && (
            <p className="mt-0.5 text-xs leading-5 text-slate-600 [overflow-wrap:anywhere] sm:text-sm">
              {userDetails.specialties.map((s) => s.name).join(" • ")}
            </p>
          )}
          {(() => {
            if (!organizationBusinessUnitId) {
              return null;
            }
            const p = thread.user_participants[0];
            const au =
              p && "assignedUser" in p
                ? (
                    p as {
                      assignedUser?: {
                        id: string;
                        firstName: string | null;
                        lastName: string | null;
                      } | null;
                    }
                  ).assignedUser
                : null;
            if (!au || au.id === currentUser?.id) {
              return null;
            }
            return (
              <div className="mt-1.5 inline-flex max-w-full items-start gap-1.5 rounded-lg border border-red-200 bg-red-50 px-2 py-1 text-xs leading-5 text-red-700">
                <UserCircle className="h-3.5 w-3.5 shrink-0" />
                <span className="min-w-0 [overflow-wrap:anywhere]">
                  Assigned to{" "}
                  <span className="font-semibold">
                    {au.firstName} {au.lastName}
                  </span>
                </span>
              </div>
            );
          })()}
        </div>
        {userDetails && (
          <Button
            variant="outline"
            className="h-12 w-12 shrink-0 gap-2 px-0 sm:w-auto sm:px-3"
            aria-label={
              showDetails ? "Hide provider details" : "Show provider details"
            }
            aria-expanded={showDetails}
            onClick={() => setShowDetails(!showDetails)}
          >
            <User aria-hidden="true" className="h-4 w-4" />
            <span className="hidden sm:inline">
              {showDetails ? "Hide" : "Details"}
            </span>
          </Button>
        )}

        {!isVirtual && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                className="h-12 w-12 shrink-0 rounded-xl text-slate-600 hover:bg-slate-100"
                aria-label="Conversation options"
              >
                <MoreVertical className="h-5 w-5" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent
              align="end"
              collisionPadding={16}
              className="max-h-[var(--radix-dropdown-menu-content-available-height)] max-w-[calc(100vw-2rem)] overflow-y-auto rounded-xl border-slate-200 bg-white p-1.5"
            >
              {shouldShowReminder() && (
                <DropdownMenuItem
                  className="min-h-12 rounded-lg text-slate-700 focus:bg-primary/5 focus:text-primary"
                  onClick={handleSendReminder}
                  disabled={
                    timeUntilNextReminder !== null ||
                    sendReminderMutation.isPending
                  }
                >
                  {sendReminderMutation.isPending ? (
                    <>
                      <Clock className="mr-2 h-4 w-4 animate-spin" />
                      Sending Reminder...
                    </>
                  ) : timeUntilNextReminder ? (
                    <>
                      <Clock className="mr-2 h-4 w-4" />
                      Wait {formatTimeRemaining(timeUntilNextReminder)}
                    </>
                  ) : (
                    <>
                      <Bell className="mr-2 h-4 w-4" />
                      Send Reminder
                    </>
                  )}
                </DropdownMenuItem>
              )}
              <DropdownMenuItem
                className="min-h-12 rounded-lg text-slate-700 focus:bg-primary/5 focus:text-primary"
                onClick={onArchive}
              >
                <Archive className="mr-2 h-4 w-4" />
                Archive Chat
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      </div>

      {userDetails && showDetails && (
        <div className="mt-3 min-w-0">
          <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
            <div className="flex flex-col gap-y-3 text-sm">
              {userDetails.locations && userDetails.locations.length > 0 && (
                <div className="flex flex-wrap items-start gap-1">
                  <span className="font-semibold text-slate-800 min-w-fit">
                    Locations:
                  </span>
                  <span className="min-w-0 flex-1 text-slate-600 [overflow-wrap:anywhere]">
                    {userDetails.locations
                      .filter((loc) => loc.name !== "ANY_CITY")
                      .map((loc) => loc.name)
                      .join(" • ")}
                    {userDetails.locations.some(
                      (loc) => loc.name === "ANY_CITY",
                    ) &&
                      (userDetails.locations.filter(
                        (loc) => loc.name !== "ANY_CITY",
                      ).length > 0
                        ? " • Open to other locations"
                        : "Any location")}
                  </span>
                </div>
              )}
              <div className="flex flex-wrap items-start gap-1">
                <span className="font-semibold text-slate-800 min-w-fit">
                  Employment types:
                </span>
                <span className="text-slate-600">
                  {userDetails.employmentTypes.length === 0
                    ? "Any Type"
                    : userDetails.employmentTypes
                        .map((type) => type.displayText)
                        .join(" • ")}
                </span>
              </div>
              {userDetails.availabilityDescription && (
                <div className="flex flex-wrap items-start gap-1">
                  <span className="font-semibold text-slate-800 min-w-fit">
                    Availability:
                  </span>
                  <span className="min-w-0 flex-1 text-slate-600 [overflow-wrap:anywhere]">
                    {userDetails.availabilityDescription}
                  </span>
                </div>
              )}
              {userDetails.previousOrganizations &&
                userDetails.previousOrganizations.length > 0 && (
                  <div className="flex flex-wrap items-start gap-1">
                    <span className="font-semibold text-slate-800 min-w-fit">
                      Organizations:
                    </span>
                    <span className="min-w-0 flex-1 text-slate-600 [overflow-wrap:anywhere]">
                      {userDetails.previousOrganizations
                        .map((o) => o.value)
                        .join(" • ")}
                    </span>
                  </div>
                )}
              <div className="mt-3 pt-3 border-t border-slate-200 flex flex-wrap gap-2">
                <Button
                  variant="outline"
                  className="h-auto min-h-12 gap-2 whitespace-normal px-4 text-sm"
                  onClick={() => setShowProfileModal(true)}
                >
                  <ExternalLink className="h-4 w-4" />
                  View Full Profile
                </Button>
                {organizationBusinessUnitId && hasUserParticipants && (
                  <Button
                    variant="outline"
                    className="h-auto min-h-12 gap-2 whitespace-normal px-4 text-sm"
                    onClick={() => setShowInterestedJobPosts(true)}
                  >
                    <Briefcase className="h-4 w-4" />
                    Job Interests
                  </Button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      <AlertDialog
        open={showReminderDialog}
        onOpenChange={setShowReminderDialog}
      >
        <AlertDialogContent className={CHAT_ALERT_CONTENT_CLASS_NAME}>
          <AlertDialogHeader className={CHAT_DIALOG_HEADER_CLASS_NAME}>
            <AlertDialogTitle>Send Reminder</AlertDialogTitle>
            <AlertDialogDescription
              className={CHAT_DIALOG_DESCRIPTION_CLASS_NAME}
            >
              This will send a notification to{" "}
              {thread.user_participants[0]?.first_name}{" "}
              {thread.user_participants[0]?.last_name} about your unread
              message. You can only send one reminder per hour.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className={CHAT_DIALOG_FOOTER_CLASS_NAME}>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmSendReminder}>
              Send Reminder
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {showProfileModal && (
        <ProfileModal
          userId={thread.user_participants[0]?.id}
          onClose={() => setShowProfileModal(false)}
          organizationBusinessUnitId={organizationBusinessUnitId}
        />
      )}
      {(() => {
        const participant = thread.user_participants[0];
        if (!organizationBusinessUnitId || !participant?.id) {
          return null;
        }
        return (
          <InterestedJobPostsDialog
            userId={participant.id}
            organizationBusinessUnitId={organizationBusinessUnitId}
            providerName={
              [participant.first_name, participant.last_name]
                .filter(Boolean)
                .join(" ") || "Provider"
            }
            open={showInterestedJobPosts}
            onOpenChange={setShowInterestedJobPosts}
          />
        );
      })()}
    </div>
  );
};
